import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

export function useTokenRestriction() {
  const location = useLocation();
  const [isRestricted, setIsRestricted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [allowedRoutes, setAllowedRoutes] = useState<string[] | null>(null);

  // Fetch allowed_routes once on mount
  useEffect(() => {
    const fetchAllowedRoutes = async () => {
      const storedToken = localStorage.getItem('solveiagro_access_token');
      
      if (!storedToken) {
        setAllowedRoutes(null);
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('access_tokens')
          .select('allowed_routes')
          .eq('token', storedToken)
          .eq('is_active', true)
          .maybeSingle();

        if (error || !data) {
          setAllowedRoutes(null);
          setIsLoading(false);
          return;
        }

        // allowed_routes is jsonb - null means no restrictions
        const routes = data.allowed_routes as string[] | null;
        setAllowedRoutes(routes);
      } catch {
        setAllowedRoutes(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllowedRoutes();
  }, []);

  // Check restriction whenever route or allowedRoutes change
  useEffect(() => {
    if (isLoading) return;

    // No token or no route restrictions = not restricted
    if (!allowedRoutes || allowedRoutes.length === 0) {
      setIsRestricted(false);
      return;
    }

    const currentPath = location.pathname;
    const isAllowed = allowedRoutes.some(route =>
      currentPath === route || currentPath.startsWith(route + '/')
    );

    setIsRestricted(!isAllowed);
  }, [location.pathname, allowedRoutes, isLoading]);

  return { isRestricted, isLoading };
}
